import { defineStore } from 'pinia';
import { Api } from '@/Api';
import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';
import { AiChapters, useAiMetadataStore } from './useAiMetadataStore';
import { ItemTranscription, ItemTranscriptionEdit, useTranscriptionStore } from './useTranscriptionStore';
import { useViewerItemStore } from './useViewerItemStore';
import { ORIGINAL_LANGUAGE, browserLanguage } from '../composables/useLanguages';

export enum ItemTranslationStatus {
  PENDING = 'pending',
  WORKING = 'working',
  DONE = 'done',
  FAILED = 'failed',
  EDITED = 'edited',
}

export interface ItemTranslationAttributes {
  language: string;
  status: ItemTranslationStatus;
  title: string | null;
  description: string | null;
  chapters: AiChapters[] | null;
  transcription: ItemTranscription[] | null;
  createdAt: string;
  updatedAt: string;
}

export interface ItemTranslationPayload {
  id: string;
  attributes: ItemTranslationAttributes;
  links?: {
    captionsUrl?: string;
  };
}

export interface ItemTranslationEdit {
  title?: string;
  description?: string;
  transcription?: ItemTranscription[];
}

interface OriginalContent {
  title?: string;
  description?: string;
  chapters?: AiChapters[];
  transcription?: ItemTranscriptionEdit[];
}

export interface ItemTranslationState {
  itemId?: string;
  availableLanguages: string[];
  statuses: Record<string, ItemTranslationStatus>;
  selectedLanguage: string | null;
  translation: ItemTranslationPayload | null;
  original: OriginalContent | null;
  isContentSwapped: boolean;
  isLoading: boolean;
  error: string | null;
  // The language an explicit pick should auto-enable captions for once its
  // translation and caption file are both ready. Cleared once applied, or as
  // soon as the viewer takes manual control of captions in the meantime.
  pendingCaptionsLanguage: string | null;
}

export const TRANSLATION_FEATURE_FLAG = 'viewer-ai-translation';

const READY_STATUSES = [ItemTranslationStatus.DONE, ItemTranslationStatus.EDITED];
const STORAGE_KEY_PREFIX = 'viewer-translation-language:';
const { debug } = useDebug(DEBUG_NAMESPACE.STORE_TRANSLATION);

const readStoredSelection = (itemId: string): string | null => {
  try {
    return window.localStorage.getItem(`${STORAGE_KEY_PREFIX}${itemId}`);
  } catch {
    return null;
  }
};

const writeStoredSelection = (itemId: string, language: string | null) => {
  try {
    if (language) {
      window.localStorage.setItem(`${STORAGE_KEY_PREFIX}${itemId}`, language);
    } else {
      window.localStorage.removeItem(`${STORAGE_KEY_PREFIX}${itemId}`);
    }
  } catch {
    // Storage can be unavailable (private mode, embeds); the preference is best effort.
  }
};

// The job translates items.name, which carries the file extension.
const stripExtension = (title: string, fileExt?: string): string => {
  if (fileExt && title.toLowerCase().endsWith(fileExt.toLowerCase())) {
    return title.slice(0, -fileExt.length);
  }
  return title;
};

const isReadyStatus = (status?: ItemTranslationStatus) => !!status && READY_STATUSES.includes(status);

export const useTranslationStore = defineStore('translationStore', {
  state: () =>
    ({
      itemId: undefined,
      availableLanguages: [],
      statuses: {},
      selectedLanguage: null,
      translation: null,
      original: null,
      isContentSwapped: false,
      isLoading: false,
      error: null,
      pendingCaptionsLanguage: null,
    }) as ItemTranslationState,
  actions: {
    async initialize(itemId: string) {
      this.$reset();
      this.itemId = itemId;

      try {
        const response = await Api.item.translations.list(itemId);
        // The viewer can switch items while this is in flight; a late response
        // must not seed another item's language list.
        if (this.itemId !== itemId) {
          return;
        }
        this.availableLanguages = response?.meta?.availableLanguages ?? [];
        // The collection key is omitted entirely when the item has no rows.
        const rows: ItemTranslationPayload[] = response?.data?.itemTranslations ?? [];
        this.statuses = Object.fromEntries(rows.map(row => [row.attributes.language, row.attributes.status]));
      } catch (e) {
        debug('Cannot load translations', e);
        return;
      }

      await this.applyPreferredLanguage();
    },
    // Never generate from the browser locale: only a cached row may be preselected,
    // and a remembered choice is honoured only while its row still exists.
    async applyPreferredLanguage() {
      if (!this.itemId) {
        return;
      }

      const candidates = [readStoredSelection(this.itemId), browserLanguage()];
      const preferred = candidates.find(language => language && this.isReady(language));
      if (preferred) {
        await this.select(preferred);
      }
    },
    async select(language: string, { explicit = false }: { explicit?: boolean } = {}) {
      if (!this.itemId) {
        return;
      }

      const itemStore = useViewerItemStore();
      const isOriginal = language === ORIGINAL_LANGUAGE || language === itemStore.originalLanguage;

      if (explicit) {
        writeStoredSelection(this.itemId, isOriginal ? null : language);
        // Captions already showing follow the pick immediately, the same as
        // before. Captions off wait for syncCaptionsLanguage to have somewhere
        // to sync from, so the auto-enable path below carries them instead.
        const wasShowing = !!itemStore.storedPlayerCaptionsLanguage;
        this.syncCaptionsLanguage(isOriginal ? itemStore.originalLanguage || 'en' : language);
        this.pendingCaptionsLanguage = !isOriginal && !wasShowing ? language : null;
      }

      if (isOriginal) {
        this.restoreOriginal();
        return;
      }

      // Re-selecting the displayed or awaited language changes nothing else. Resetting
      // would turn isTranslationActive off mid-fetch and route an edit onto the item,
      // but a queued auto-enable still needs to be resolved against what's on screen.
      if (language === this.selectedLanguage) {
        if (this.translation) {
          this.maybeAutoEnableCaptions(this.translation);
        }
        return;
      }

      this.snapshotOriginal();
      this.selectedLanguage = language;
      this.translation = null;
      this.error = null;
      this.isLoading = true;

      try {
        const payload = await this.fetchOrRequest(language, explicit);
        if (this.selectedLanguage !== language) {
          return;
        }
        this.handlePayload(payload);
      } catch (e: any) {
        if (this.selectedLanguage !== language) {
          return;
        }
        debug('Cannot select translation', e);
        this.fail(e?.message || 'Translation is not available');
      }
    },
    // A missing row is requested; a failed row is requested again only on an
    // explicit click, which is the retry the viewer offers.
    async fetchOrRequest(language: string, explicit: boolean): Promise<ItemTranslationPayload | null> {
      const existing = await this.fetchRow(language);
      const shouldRequest = !existing || (explicit && existing.attributes.status === ItemTranslationStatus.FAILED);
      if (!shouldRequest) {
        return existing;
      }

      const response = await Api.item.translations.create(this.itemId as string, language);
      if (response?.meta?.originalLanguage) {
        return null;
      }
      return response?.data?.itemTranslation ?? null;
    },
    async fetchRow(language: string): Promise<ItemTranslationPayload | null> {
      try {
        const response = await Api.item.translations.get(this.itemId as string, language);
        return response?.data?.itemTranslation ?? null;
      } catch (e: any) {
        if (e?.httpStatus === 404) {
          return null;
        }
        throw e;
      }
    },
    handlePayload(payload: ItemTranslationPayload | null) {
      // No row means the server treats this as the item's own language.
      if (!payload) {
        this.restoreOriginal();
        return;
      }

      const { language, status } = payload.attributes;
      this.statuses[language] = status;

      if (isReadyStatus(status)) {
        this.applyTranslation(payload);
        return;
      }

      if (status === ItemTranslationStatus.FAILED) {
        this.fail('Translation failed');
        return;
      }

      this.translation = payload;
      this.isLoading = true;
    },
    async refreshSelected() {
      const language = this.selectedLanguage;
      if (!language) {
        return;
      }

      try {
        const payload = await this.fetchRow(language);
        if (payload && this.selectedLanguage === language) {
          this.handlePayload(payload);
        }
      } catch (e) {
        debug('Cannot refresh translation', e);
      }
    },
    async onStatusEvent(attributes?: { language?: string; status?: ItemTranslationStatus }) {
      if (!attributes?.language || !attributes.status) {
        return;
      }

      this.statuses[attributes.language] = attributes.status;
      if (attributes.language !== this.selectedLanguage) {
        return;
      }

      if (isReadyStatus(attributes.status)) {
        await this.refreshSelected();
      } else if (attributes.status === ItemTranslationStatus.FAILED) {
        this.fail('Translation failed');
      }
    },
    async onCaptionGenerated(attributes?: { language?: string }) {
      if (attributes?.language && attributes.language === this.selectedLanguage) {
        await this.refreshSelected();
      }
    },
    // The row is gone because the item turned out to be in that language already,
    // so the original is what the viewer asked for.
    onRemoved(attributes?: { language?: string }) {
      const language = attributes?.language;
      if (!language) {
        return;
      }

      delete this.statuses[language];
      if (language === this.selectedLanguage) {
        this.restoreOriginal();
      }
    },
    applyTranslation(payload: ItemTranslationPayload) {
      const itemStore = useViewerItemStore();
      const aiMetadataStore = useAiMetadataStore();
      const transcriptionStore = useTranscriptionStore();

      this.translation = payload;
      this.isLoading = false;
      this.error = null;

      const { title, description, chapters, transcription } = payload.attributes;
      this.isContentSwapped = true;
      if (title) {
        itemStore.setDisplayName(stripExtension(title, itemStore.item?.attributes.fileExt));
      }
      if (description) {
        itemStore.setDescription(description);
      }
      if (chapters) {
        aiMetadataStore.$patch({ chapters });
      }
      if (transcription) {
        transcriptionStore.$patch({ transcription: { data: transcription.map(segment => ({ ...segment })) } });
      }

      this.maybeAutoEnableCaptions(payload);
    },
    // The caption file is a second, independently-timed readiness signal
    // (item_translation_caption_generated), so a translation whose content is
    // ready may not have one yet; auto-enable only fires once both exist.
    maybeAutoEnableCaptions(payload: ItemTranslationPayload) {
      if (this.pendingCaptionsLanguage !== payload.attributes.language || !payload.links?.captionsUrl) {
        return;
      }

      this.pendingCaptionsLanguage = null;
      useViewerItemStore().storeVideoPlayerOptions({ captionsLanguage: payload.attributes.language });
    },
    // The viewer took manual control of captions (via the native CC menu),
    // which overrides any auto-enable an earlier explicit language pick queued.
    cancelPendingCaptions() {
      this.pendingCaptionsLanguage = null;
    },
    snapshotOriginal() {
      if (this.original) {
        return;
      }

      const itemStore = useViewerItemStore();
      const aiMetadataStore = useAiMetadataStore();
      const transcriptionStore = useTranscriptionStore();
      const transcription = transcriptionStore.transcriptionData;

      this.original = {
        title: itemStore.displayName,
        description: itemStore.description,
        chapters: aiMetadataStore.chapters ? [...aiMetadataStore.chapters] : undefined,
        transcription: transcription && transcription.length > 0 ? transcription.map(segment => ({ ...segment })) : undefined,
      };
    },
    restoreOriginal() {
      this.selectedLanguage = null;
      this.isContentSwapped = false;
      this.translation = null;
      this.isLoading = false;
      this.error = null;

      if (!this.original) {
        return;
      }

      const itemStore = useViewerItemStore();
      const aiMetadataStore = useAiMetadataStore();
      const transcriptionStore = useTranscriptionStore();
      const { title, description, chapters, transcription } = this.original;
      this.original = null;

      if (title !== undefined) {
        itemStore.setDisplayName(title);
      }
      if (description !== undefined) {
        itemStore.setDescription(description);
      }
      aiMetadataStore.$patch({ chapters });

      // The transcript loads lazily, so an empty snapshot means it was never
      // fetched; refetch the original instead of restoring nothing.
      if (transcription) {
        transcriptionStore.$patch({ transcription: { data: transcription } });
      } else if (transcriptionStore.transcriptionId) {
        transcriptionStore.loadTranscription(transcriptionStore.transcriptionId);
      }
    },
    // A full item refresh (pusher item_update, a layout save, an editor result)
    // carries the original-language title and description. While a language is
    // selected they are the new original to restore to, not what to display:
    // letting them through would show English under an active translation and
    // route the owner's next edit, with that English text, onto the translation row.
    onItemReplaced(attributes?: { nameWithoutExtension?: string; description?: string }) {
      if (!this.original || !attributes) {
        return;
      }

      if (attributes.nameWithoutExtension !== undefined) {
        this.original.title = attributes.nameWithoutExtension;
      }
      if (attributes.description !== undefined) {
        this.original.description = attributes.description;
      }

      if (!this.isTranslationActive) {
        return;
      }

      const itemStore = useViewerItemStore();
      const { title, description } = this.translation!.attributes;
      if (title) {
        itemStore.setDisplayName(stripExtension(title, itemStore.item?.attributes.fileExt));
      }
      if (description) {
        itemStore.setDescription(description);
      }
    },
    async updateContent(edit: ItemTranslationEdit) {
      if (!this.itemId || !this.selectedLanguage) {
        return Promise.reject(new Error('No translation selected'));
      }

      const response = await Api.item.translations.update(this.itemId, this.selectedLanguage, edit);
      const payload = response?.data?.itemTranslation;
      if (payload) {
        this.applyTranslation(payload);
      }
    },
    // The previous language's content can still be on screen here, and
    // isTranslationActive turns false, which would route the owner's next edit
    // onto the item and overwrite it with the translated text.
    fail(message: string) {
      if (this.isContentSwapped) {
        this.restoreOriginal();
      } else {
        // The original is on screen and the owner can edit it, so the snapshot
        // taken at selection is stale; the next selection takes a fresh one.
        this.original = null;
      }

      // Original content is what shows now, so the selector must not keep
      // labelling the failed language as the current one.
      this.selectedLanguage = null;
      this.translation = null;
      this.isLoading = false;
      this.error = message;
    },
    syncCaptionsLanguage(language: string) {
      const itemStore = useViewerItemStore();
      if (itemStore.storedPlayerCaptionsLanguage) {
        itemStore.storeVideoPlayerOptions({ captionsLanguage: language });
      }
    },
  },
  getters: {
    isTranslationActive: (state: ItemTranslationState) => !!state.translation && isReadyStatus(state.translation.attributes.status),
    activeTranscription(state: ItemTranslationState): ItemTranscription[] | null {
      return this.isTranslationActive ? (state.translation?.attributes.transcription ?? null) : null;
    },
    captionsUrl: (state: ItemTranslationState) => state.translation?.links?.captionsUrl,
    isReady: (state: ItemTranslationState) => (language: string) => isReadyStatus(state.statuses[language]),
  },
});
